import {plural} from "@lingui/core/macro";
import * as React from "react";

import type {Assignment} from "@khan/assignments-types";

import ArchiveAssignmentNotification from "./archive-assignment-notification.tsx";
import ArchiveAssignmentsConfirmationModal from "./archive-assignments-confirmation-modal.tsx";
import ArchiveContentAssignmentsMutation, {
    type ArchiveContentAssignmentsAction,
    type ArchiveContentAssignmentsResult,
} from "./archive-content-assignments-mutation.tsx";

interface Props {
    assignmentIds: Array<string>;
    onClose: () => void;
    onSuccess: (archivedAssignments: Array<string>) => void;
}

const ArchiveContentAssignmentsModal = (props: Props) => {
    const notifyToast = (archivedAssignments: ReadonlyArray<Assignment>) => {
        const {assignmentIds} = props;
        const successCount = archivedAssignments.length;
        const failedCount = assignmentIds.length - successCount;
        const studentListName =
            (archivedAssignments[0] &&
                archivedAssignments[0].studentList &&
                archivedAssignments[0].studentList.name) ||
            null;
        const isChildAssignmentsClassroom =
            !!archivedAssignments[0]?.studentList?.isChildAssignmentsClassroom;

        import("@khan/component-toast").then(({renderToast}) => {
            renderToast(
                <ArchiveAssignmentNotification
                    successCount={successCount}
                    failedCount={failedCount}
                    studentListName={studentListName ?? ""}
                    isChildAssignmentsClassroom={isChildAssignmentsClassroom}
                />,
            );
        });
    };

    const handleArchiveAssignmentsSuccess = (
        archivedAssignments: ReadonlyArray<Assignment>,
    ) => {
        notifyToast(archivedAssignments);
        props.onSuccess(
            archivedAssignments.map((assignment) => assignment.id || ""),
        );
        props.onClose();
    };

    const handleArchiveAssignmentsError = () => {
        notifyToast([]);
    };

    const renderModal = (
        archiveAssignmentsAction: ArchiveContentAssignmentsAction,
        {isArchiving}: ArchiveContentAssignmentsResult,
    ) => {
        const {assignmentIds, onClose} = props;

        const title = plural(assignmentIds.length, {
            one: "Delete # assignment?",
            other: "Delete # assignments?",
        });

        const bodyText = plural(assignmentIds.length, {
            one: "# assignment will be removed from your class. Completion and reports will also be deleted.",
            other: "# assignments will be removed from your class. Completion and reports will also be deleted.",
        });

        return (
            <ArchiveAssignmentsConfirmationModal
                title={title}
                bodyText={bodyText}
                assignmentIds={assignmentIds}
                onClose={onClose}
                archiveAssignments={archiveAssignmentsAction}
                isArchiving={isArchiving}
            />
        );
    };

    return (
        <ArchiveContentAssignmentsMutation
            onSuccess={handleArchiveAssignmentsSuccess}
            onError={handleArchiveAssignmentsError}
        >
            {renderModal}
        </ArchiveContentAssignmentsMutation>
    );
};

export default ArchiveContentAssignmentsModal;
